// diff-markdown.js -- Peteramati JavaScript library
// Peteramati is Copyright (c) 2006-2024 Eddie Kohler
// See LICENSE for open-source distribution terms

import { Filediff } from "./diff.js";
import { html_id_encode } from "./encoders.js";
import { active_scroll_anchor } from "./note-edit.js";
import { hasClass, addClass, removeClass, toggleClass, handle_ui } from "./ui.js";
import { hoturl } from "./hoturl.js";
import { markdownit_minihtml } from "./markdown-minihtml.js";
import { markdownit_katex } from "./markdown-katex.js";
import { markdownit_deflist } from "./markdown-deflist.js";
import { minihighlight, minihighlight_supports, minihighlight_summary_state, minihighlight_state_equals } from "./minihighlight.js";

let md, mdcontext;
const nonwsre = /[^ \t\r\n]/;


function hljs_line(s, lang) {
    try {
        const result = hljs.highlight(s, {language: lang, ignoreIllegals: true}),
            ns = result.value;
        if (s.endsWith("\r\n") && ns.endsWith("\n\n")) {
            result.value = ns.substring(0, ns.length - 1);
        }
        return result;
    } catch {
        return null;
    }
}

function render_map(map) {
    if (map[0] + 1 === map[1]) {
        return String(map[1]);
    }
    return (map[0] + 1) + "-" + map[1];
}

function add_landmark(tokens, idx, options, env, self) {
    const token = tokens[idx];
    if (token.map && token.level === 0) {
        token.attrSet("data-landmark", render_map(token.map));
    }
    return self.renderToken(tokens, idx, options, env);
}

function add_landmark_1(tokens, idx, options, env, self) {
    const token = tokens[idx];
    if (token.map && token.level <= 1) {
        token.attrSet("data-landmark", render_map(token.map));
    }
    return self.renderToken(tokens, idx, options, env);
}

function fix_landmark_html(html, token, level) {
    if (token.map && token.level <= level) {
        let lm = " data-landmark=\"" + render_map(token.map) + "\"",
            sp = html.indexOf(" "),
            gt = html.indexOf(">");
        if (sp > 0 && sp < gt) {
            gt = sp;
        }
        html = html.substring(0, gt) + lm + html.substring(gt);
    }
    return html;
}

function modify_landmark(base, level) {
    if (!base) {
        return level === 0 ? add_landmark : add_landmark_1;
    }
    return function (tokens, idx, options, env, self) {
        const token = tokens[idx];
        return fix_landmark_html(base(tokens, idx, options, env, self), token, level);
    };
}

// Resolve `url` against the directory of the Markdown file being rendered.
// Returns a path relative to the repository root, or null if `url` does not
// name a file in this repository.
function repo_path(pi, url) {
    let m, m2;
    if (/\/\//.test(url)) {
        if ((m = url.match(/^https:\/\/github\.com\/([^/]+\/[^/]+)\/(?:blob|raw)\/([^/]+)\/(.*)$/))
            && (m2 = (pi.getAttribute("data-pa-repourl") || "").match(/^(?:https:\/\/github\.com\/|git@github\.com:)(.*?)\/?$/))
            && m2[1] === m[1]
            && pi.getAttribute("data-pa-branch") === m[2]) {
            return m[3];
        }
        return null;
    }
    const fd = Filediff.closest(mdcontext),
        slash = fd ? fd.file.lastIndexOf("/") : -1;
    let dir = slash < 0 ? "" : fd.file.substring(0, slash);
    while (true) {
        if (url.startsWith("./")) {
            url = url.substring(2).replace(/^\/+/, "");
        } else if (url.startsWith("../") && dir !== "") {
            url = url.substring(3).replace(/^\/+/, "");
            dir = dir.replace(/(?:^|\/)[^/]+\/*$/, "");
        } else if (url.startsWith("../") || url.startsWith("/")) {
            return null;
        } else if ((m = url.match(/(^|\/+)[^/]+\/\.\.(?:\/+|$)(.*)$/))) {
            url = m[1] + m[2];
        } else {
            break;
        }
    }
    if (url === "") {
        return null;
    }
    return dir ? dir + "/" + url : url;
}

/** @param {string} file
 * @return {string} */
function repo_raw_url(pi, file) {
    const user = pi.getAttribute("data-pa-user") || siteinfo.uservalue;
    let t = siteinfo.site_relative;
    if (user) {
        t += "~" + encodeURIComponent(user) + "/";
    }
    t += "raw/" + pi.getAttribute("data-pa-pset") + "/" + pi.getAttribute("data-pa-commit") + "/" + file;
    if (siteinfo.defaults) {
        t += "?" + (new URLSearchParams(siteinfo.defaults)).toString();
    }
    return t;
}

function modify_landmark_image(base) {
    return function (tokens, idx, options, env, self) {
        const token = tokens[idx],
            srci = token.attrIndex("src");
        let pi;
        if (mdcontext && (pi = mdcontext.closest(".pa-psetinfo"))) {
            const src = token.attrs[srci][1],
                path = repo_path(pi, src);
            if (path !== null) {
                token.attrs[srci][1] = repo_raw_url(pi, path);
            } else if (!/\/\//.test(src)) {
                token.attrs[srci][1] = "data:image/jpg,";
            }
        }
        return fix_landmark_html(base(tokens, idx, options, env, self), token);
    };
}

// Point a link at a repository file at that file's diff, if it is displayed,
// and at its raw contents otherwise.
function modify_repo_link(base) {
    return function (tokens, idx, options, env, self) {
        const token = tokens[idx],
            hrefi = token.attrIndex("href");
        let pi;
        if (hrefi >= 0 && mdcontext && (pi = mdcontext.closest(".pa-psetinfo"))) {
            const m = token.attrs[hrefi][1].match(/^([^#?]*)(?:[#?].*)?$/),
                path = m[1] === "" ? null : repo_path(pi, m[1]);
            if (path !== null) {
                token.attrs[hrefi][1] = Filediff.by_file(path)
                    ? "#F" + html_id_encode(path)
                    : repo_raw_url(pi, path);
            }
        }
        return base ? base(tokens, idx, options, env, self)
            : self.renderToken(tokens, idx, options, env);
    };
}

function render_landmark_fence(md) {
    return function (tokens, idx, options, env, self) {
        const token = tokens[idx];
        let info = token.info ? md.utils.unescapeAll(token.info) : "", m;
        if (info && info.indexOf(" ") >= 0) {
            if ((m = info.match(/^ *([-a-z+]+) *$/))) {
                info = m[1];
            } else {
                token.content = info + "\n" + token.content;
                token.map && (token.map[0] -= 1);
                info = "";
            }
        }
        let lang = info ? info.trim().split(/\s+/g)[0] : null;
        const ismini = minihighlight_supports(lang);
        if (lang && !ismini && !hljs.getLanguage(lang)) {
            lang = null;
        }
        const linefn = ismini ? minihighlight : hljs_line;

        let xtoken = token;
        if (lang) {
            let i = token.attrIndex("class");
            xtoken = {attrs: token.attrs ? token.attrs.slice() : []};
            if (i < 0) {
                xtoken.attrs.push(["class", options.langPrefix + lang]);
            } else {
                xtoken.attrs[i][1] += " " + options.langPrefix + lang;
            }
        }
        let xattrs = '><code'.concat(self.renderAttrs(xtoken), '>');

        if (token.map && token.level === 0) {
            // split into lines, assign landmarks
            const x = token.content.split(/\n/), y = [];
            x[x.length - 1] === "" && x.pop();
            const xl = x.length;
            let i = 0, ln0 = token.map[0] + 2, hlstate = null;
            while (lang && i !== xl) {
                const result = linefn(x[i] + "\n", lang, hlstate);
                if (result) {
                    y.push('<pre data-landmark="', ln0 + i,
                           i + 1 !== xl ? '" class="partial"' : '"',
                           xattrs, result.value, "</code></pre>");
                    hlstate = result.top;
                    ++i;
                } else {
                    break;
                }
            }
            while (i !== xl) {
                y.push('<pre data-landmark="', ln0 + i,
                       i + 1 !== xl ? '" class="partial"' : '"',
                       xattrs, x[i], "\n</code></pre>");
                ++i;
            }
            return y.join("");
        } else {
            return '<pre'.concat(xattrs, md.utils.escapeHtml(token.content), '</code></pre>');
        }
    };
}

function make_markdownit() {
    if (!md) {
        md = markdownit('hotcrp', {linkify: true})
            .use(markdownit_katex)
            .use(markdownit_minihtml)
            .use(markdownit_deflist);
        for (let x of ["paragraph_open", "heading_open", "ordered_list_open",
                       "bullet_list_open", "dl_open", "table_open",
                       "blockquote_open", "math_block", "hr"]) {
            md.renderer.rules[x] = modify_landmark(md.renderer.rules[x], 0);
        }
        for (let x of ["list_item_open", "dt_open", "dd_open"]) {
            md.renderer.rules[x] = modify_landmark(md.renderer.rules[x], 1);
        }
        md.renderer.rules.fence = md.renderer.rules.code_block = render_landmark_fence(md);
        md.renderer.rules.image = modify_landmark_image(md.renderer.rules.image);
        md.renderer.rules.link_open = modify_repo_link(md.renderer.rules.link_open);
    }
    return md;
}

function fix_list_item(d) {
    let dc;
    while ((dc = d.firstChild) && dc.nodeType !== 1) {
        d.removeChild(dc);
    }
    if (!dc || !dc.hasAttribute("data-landmark")) {
        return d;
    }
    while (dc.nextSibling && dc.nextSibling.nodeType !== 1) {
        d.removeChild(dc.nextSibling);
    }
    if (dc.nextSibling) {
        if (d.tagName === "OL") {
            if (!dc.hasAttribute("value")) {
                dc.value = d.start;
            }
            d.start = dc.value + 1;
        }
        let nd = document.createElement(d.tagName);
        nd.appendChild(d.removeChild(dc));
        d = nd;
    }
    d.setAttribute("data-landmark", dc.getAttribute("data-landmark"));
    return d;
}

function fix_dl_item(d) {
    let dc;
    while ((dc = d.firstChild) && dc.nodeType !== 1) {
        d.removeChild(dc);
    }
    if (!dc || !dc.hasAttribute("data-landmark")) {
        return d;
    }
    let seen_dd = dc.tagName === "DD";
    while (true) {
        let dn = dc.nextSibling;
        if (dn && dn.nodeType !== 1) {
            d.removeChild(dn);
            continue;
        }
        dc = dn;
        if (!dc || (seen_dd && dc.tagName === "DT")) {
            break;
        } else if (dc.tagName === "DD") {
            seen_dd = true;
        }
    }
    if (dc) {
        let nd = document.createElement(d.tagName);
        while (d.firstChild !== dc) {
            nd.appendChild(d.removeChild(d.firstChild));
        }
        d = nd;
    }
    d.setAttribute("data-landmark", d.firstChild.getAttribute("data-landmark"));
    return d;
}

Filediff.define_method("markdown", function () {
    const elt = this.element;
    if (hasClass(elt, "pa-markdown")
        || hasClass(elt, "pa-highlight")) {
        return;
    }
    const hidelm = hasClass(elt, "pa-hide-landmarks"),
        widthcode = +elt.getAttribute("data-pa-widthcode"),
        drclass = (hidelm ? "pa-dx pa-dhlm pa-dr" : "pa-dx pa-dr") +
            (widthcode ? ` pa-dw${widthcode}` : "");
    // collect content
    let e = elt.firstChild, l = [], lineno = 1;
    while (e) {
        let n = e.nextSibling;
        if (hasClass(e, "pa-dlr")) {
            elt.removeChild(e);
        } else if (hasClass(e, "pa-gi") || hasClass(e, "pa-gc")) {
            const this_lineno = +e.firstChild.nextSibling.getAttribute("data-landmark");
            while (lineno < this_lineno) {
                l.push("\n");
                ++lineno;
            }
            l.push(e.lastChild.textContent);
            ++lineno;
            e.hidden = true;
        }
        e = n;
    }
    // render to markdown
    let dx = document.createElement("div"), d, lr;
    mdcontext = elt;
    dx.innerHTML = make_markdownit().render(l.join(""));
    if (l.includes("tt-test.cc")) {
    console.log(l);
}
    mdcontext = null;
    // split up and insert into order
    e = elt.firstChild;
    while ((d = dx.firstChild)) {
        if (d.nodeType !== 1) {
            dx.removeChild(d);
            continue;
        } else if (d.tagName === "OL" || d.tagName === "UL") {
            d = fix_list_item(d);
        } else if (d.tagName === "DL") {
            d = fix_dl_item(d);
        } else if (d.tagName === "P"
                   && d.firstChild.nodeType === 1
                   && d.firstChild.tagName === "IMG"
                   && d.firstChild === d.lastChild) {
            addClass(d, "image-container");
        }

        const lp = document.createElement("div");
        lp.className = "pa-dl pa-dlr";
        const la = document.createElement("div");
        la.className = e.firstChild.className;
        la.hidden = hidelm;
        const lb = document.createElement("div");
        lb.className = e.firstChild.nextSibling.className;
        lb.hidden = hidelm;

        const lm = d.getAttribute("data-landmark");
        if (lm) {
            let l1 = parseInt(lm),
                dash = lm.indexOf("-"),
                l2 = dash >= 0 ? parseInt(lm.substring(dash + 1)) : l1;
            while (e) {
                if ((hasClass(e, "pa-gi") || hasClass(e, "pa-gc"))
                    && +e.firstChild.nextSibling.getAttribute("data-landmark") >= l1) {
                    break;
                }
                e = e.nextSibling;
            }
            lb.setAttribute("data-landmark", l1);
            let klass = 0, ee = e;
            while (ee) {
                if (hasClass(ee, "pa-gi") || hasClass(ee, "pa-gc")) {
                    if (+ee.firstChild.nextSibling.getAttribute("data-landmark") >= l2) {
                        break;
                    }
                    klass |= hasClass(ee, "pa-gi") ? 1 : 2;
                }
                ee = ee.nextSibling;
            }
            lp.className += klass === 2 ? " pa-gc" : " pa-gi";
        }

        lr = document.createElement("div");
        lr.className = drclass;
        if (d === dx.firstChild) {
            dx.removeChild(d);
        }
        lr.appendChild(d);
        while (dx.firstChild && dx.firstChild.nodeType !== 1) {
            lr.appendChild(dx.removeChild(dx.firstChild));
        }

        lp.append(la, lb, lr);
        elt.insertBefore(lp, e);
    }

    if (lr) {
        addClass(lr, "pa-dr-last");
    }
    addClass(elt, "pa-markdown");
    removeClass(elt, "need-markdown");
});

Filediff.define_method("unmarkdown", function () {
    const elt = this.element;
    let e = elt.firstChild;
    while (e) {
        let n = e.nextSibling;
        if (hasClass(e, "pa-dlr")) {
            elt.removeChild(e);
        } else if (hasClass(e, "pa-gi") || hasClass(e, "pa-gc")) {
            e.hidden = false;
        }
        e = n;
    }
    removeClass(elt, "pa-markdown");
});

Filediff.define_method("highlight", function () {
    const elt = this.element;
    // compute language
    let lang = elt.getAttribute("data-language");
    if (!lang || hasClass(elt, "pa-markdown")) {
        return;
    }
    // Prefer minihighlight (fast, correct across lines);
    // fall back to highlight.js for languages it doesn't cover.
    const ismini = minihighlight_supports(lang);
    if (!ismini && !hljs.getLanguage(lang)) {
        return;
    }
    const linefn = ismini ? minihighlight : hljs_line;
    // Parse hlsummaries: compact representations of highlight-sensitive
    // multi-line constructs. This lets us correctly render, e.g., block
    // comments that started in hidden context (outside the range of the diff).
    let hlsuma = null, hlsumb = null;
    if (ismini) {
        const la = elt.getAttribute("data-pa-hlsummary-a"),
            lb = elt.getAttribute("data-pa-hlsummary-b");
        hlsuma = la ? JSON.parse(la) : null;
        hlsumb = lb ? JSON.parse(lb) : null;
    }
    // collect content
    const langclass = "language-" + lang;
    let e = elt.firstChild, hlstatei = null, hlstated = null, resync = ismini ? 3 : 0;
    for (; e; e = e.nextSibling) {
        const type = hasClass(e, "pa-gi") ? 2 : (hasClass(e, "pa-gc") ? 3 : (hasClass(e, "pa-gd") ? 1 : 0));
        if (type === 0) {
            if (hasClass(e, "pa-gx")) {     // hunk boundary: re-seed at the next code line
                resync = ismini ? 3 : 0;
            }
            continue;
        }
        if (resync & type & 1) {
            const da = e.firstChild;
            hlstated = minihighlight_summary_state(lang, hlsuma, +da.getAttribute("data-landmark"));
            resync &= ~1;
        }
        if (resync & type & 2) {
            const db = e.firstChild.nextSibling;
            hlstatei = minihighlight_summary_state(lang, hlsumb, +db.getAttribute("data-landmark"));
            resync &= ~2;
        }
        const ce = e.lastChild,
            ishl = hasClass(ce, langclass),
            s = ce.textContent,
            result = linefn(s, lang, type & 2 ? hlstatei : hlstated);
        if (!result) {
            break;
        }
        if (type & 1) {
            if ((type & 2)
                && (hlstatei || hlstated)
                && !minihighlight_state_equals(hlstatei, hlstated)) {
                hlstated = linefn(s, lang, hlstated).top;
            } else {
                hlstated = result.top;
            }
        }
        if (type & 2) {
            hlstatei = result.top;
        }
        if (ishl) {
            continue;
        }
        ce.innerHTML = result.value;
        addClass(ce, langclass);
        let state = 0;
        for (let xe = ce.firstChild; xe; xe = xe.nextSibling) {
            if ((xe.nodeType === 1 && !hasClass(xe, "hljs-comment"))
                || (xe.nodeType === 3 && nonwsre.test(xe.textContent))) {
                state = -1;
                break;
            } else if (xe.nodeType === 1) {
                state = 1;
            }
        }
        if (state === 0) {
            addClass(e, "pa-gblank");
        } else if (state === 1) {
            addClass(e, "pa-gcomment");
        }
    }
    addClass(elt, "pa-highlight");
    removeClass(elt, "need-highlight");
});

Filediff.define_method("unhighlight", function () {
    // compute language
    const elt = this.element,
        lang = elt.getAttribute("data-language"),
        langclass = lang ? "language-" + lang : "";
    let e = elt.firstChild, et;
    while (e) {
        if ((et = e.lastChild)
            && (!langclass || hasClass(et, langclass))) {
            et.replaceChildren(et.textContent);
            langclass && removeClass(et, langclass);
        }
        e = e.nextSibling;
    }
    removeClass(elt, "pa-highlight");
});

// Render (highlight or markdown) a single filediff, if it still needs it.
function render_filediff(fd) {
    if (hasClass(fd.element, "need-markdown")) {
        fd.markdown();
    } else if (hasClass(fd.element, "need-highlight")) {
        fd.highlight();
    }
}

// Highlighting and markdown rendering are expensive. On small diffs we render
// every file synchronously as it is decorated. On large diffs the synchronous
// work—much of it for files far offscreen—would block the main thread for a
// long time, so after `SYNC_RENDER_BUDGET` files we switch to an asynchronous
// queue: visible files render first (via IntersectionObserver), and the rest of
// the displayed files are backfilled during idle time. Collapsed files render
// lazily, only once they are expanded and scrolled into view.

const SYNC_RENDER_BUDGET = 25;

const request_idle = window.requestIdleCallback
    ? window.requestIdleCallback.bind(window)
    : (cb) => setTimeout(cb, 1);

class HighlightQueue {
    constructor() {
        this.jobs = new Map();      // filediff element -> Filediff (all pending)
        this.backfill = [];         // displayed pending fds, idle-rendered in order
        this.bfpos = 0;
        this.idle = false;
        // The rootMargin renders files just before they scroll into view.
        this.observer = new IntersectionObserver(this.observed.bind(this), {rootMargin: "300px 0px"});
    }
    add(fd) {
        const elt = fd.element;
        if (this.jobs.has(elt)) {
            return;
        }
        this.jobs.set(elt, fd);
        this.observer.observe(elt);
        // Collapsed files wait for IntersectionObserver to fire when shown; only
        // displayed files are backfilled during idle time.
        if (!elt.hidden) {
            this.backfill.push(fd);
            this.schedule();
        }
    }
    process(fd) {
        if (this.jobs.delete(fd.element)) {
            this.observer.unobserve(fd.element);
            render_filediff(fd);
        }
    }
    observed(entries) {
        // Privilege visible files: render them as soon as they intersect.
        for (const entry of entries) {
            if (entry.isIntersecting) {
                const fd = this.jobs.get(entry.target);
                fd && this.process(fd);
            }
        }
    }
    schedule() {
        if (!this.idle && this.bfpos < this.backfill.length) {
            this.idle = true;
            request_idle(this.run.bind(this));
        }
    }
    run(deadline) {
        this.idle = false;
        const timed = !!(deadline && deadline.timeRemaining);
        let budget = timed ? Infinity : 12;
        while (this.bfpos < this.backfill.length && budget > 0) {
            this.process(this.backfill[this.bfpos]);
            ++this.bfpos;
            --budget;
            if (timed && deadline.timeRemaining() <= 1) {
                break;
            }
        }
        this.schedule();
    }
}

let highlight_queue = null, nsync_render = 0;

Filediff.add_decorator(function (fd) {
    const elt = fd.element;
    if (hasClass(elt, "need-load")) {
        // nothing to do
    } else if (hasClass(elt, "need-markdown")) {
        render_filediff(fd);
    } else if (hasClass(elt, "need-highlight")) {
        if (!window.IntersectionObserver
            || (!elt.hidden
                && ++nsync_render <= SYNC_RENDER_BUDGET)) {
            render_filediff(fd);
        } else {
            highlight_queue = highlight_queue || new HighlightQueue;
            highlight_queue.add(fd);
        }
    }
});


handle_ui.on("pa-diff-toggle-markdown", function (evt) {
    const $es = evt.metaKey ? $(".pa-diff-toggle-markdown") : $(this),
        fd = Filediff.referenced(this),
        show = !hasClass(fd.element, "pa-markdown"),
        restore = active_scroll_anchor();
    $es.each(function () {
        const fd = Filediff.referenced(this),
            shown = hasClass(fd.element, "pa-markdown");
        if (show && !shown) {
            fd.markdown();
        } else if (!show && shown) {
            fd.unmarkdown();
        }
        toggleClass(this, "btn-primary", show);
    });
    restore();
    if (!evt.metaKey) {
        $.post(hoturl("=api/diffconfig", {psetinfo: fd.element}),
            {file: fd.file, markdown: show ? 1 : 0});
    }
});
